/**
 * Document Preview Service
 * Handles document preview functionality including SAS token retrieval
 * and document download for preview purposes
 */

import { authenticatedFetch } from '../utils/authErrorHandler';
import {
  downloadFromSharePoint,
  downloadFromSharePointWithProgress,
  hasSharePointIdentifiers,
  type DownloadProgressCallback,
  type ExtendedError,
  type SharePointDocument,
} from './sharePointDownload';

export { downloadRawFromSharePoint } from './sharePointDownload';
export type { DownloadProgressCallback } from './sharePointDownload';

interface UserDocument extends SharePointDocument {
  filePath?: string;
}

interface SASData {
  sasUrl?: string;
  filePath?: string;
  expiresAt?: string | null;
  permissions?: string;
  isSharePoint?: boolean;
  sharepointSiteId?: string;
  sharepointDriveId?: string;
  sharepointItemId?: string;
  source?: string;
}

interface DocumentPreviewResult {
  sasData: SASData;
  contentBlob: Blob;
  document: UserDocument;
}

// Get base URL for file access service
const getFileAccessBaseUrl = (): string => {
  // Check for specific file access service URL first
  const fileAccessUrl = import.meta.env.VITE_FILE_ACCESS_API_BASE_URL;
  if (fileAccessUrl) {
    return fileAccessUrl;
  }

  // Fallback to deals API base URL (assuming file access is on the same VM)
  const baseUrl = import.meta.env.VITE_DEALS_API_BASE_URL || 'https://default-api-url.com';
  return baseUrl;
};

// Office formats that Microsoft Graph can convert to PDF via ?format=pdf
const GRAPH_CONVERTIBLE_EXTENSIONS: string[] = [
  '.pptx', '.ppt', '.docx', '.doc', '.xlsx', '.xls'
];

/**
 * Check if a file path is a direct URL
 */
const isDirectUrl = (filePath: string): boolean => {
  return !!filePath && (filePath.startsWith('http://') || filePath.startsWith('https://'));
};

/**
 * Check if a file path is a SharePoint URL. Matches on the parsed hostname
 * so hosts merely containing "sharepoint.com" elsewhere do not qualify.
 */
const isSharePointUrl = (filePath: string): boolean => {
  if (!isDirectUrl(filePath)) return false;
  try {
    const { hostname } = new URL(filePath);
    return hostname === 'sharepoint.com' || hostname.endsWith('.sharepoint.com');
  } catch {
    return false;
  }
};

/**
 * Get SAS token for downloading a user document
 */
export const getDocumentDownloadSAS = async (
  userDocument: UserDocument,
  _projectId?: string,
  _username?: string
): Promise<SASData> => {
  // Priority 1: Check for SharePoint identifiers (Phase 3: Graph API path)
  if (hasSharePointIdentifiers(userDocument)) {
    // Return a marker object indicating this is a SharePoint document
    // The getDocumentPreview function will handle the actual download
    return {
      isSharePoint: true,
      sharepointSiteId: userDocument.sharepointSiteId,
      sharepointDriveId: userDocument.sharepointDriveId,
      sharepointItemId: userDocument.sharepointItemId
    };
  }

  // Priority 2: Extract filePath from document (Phase 4: filePath only contract)
  const filePath: string | undefined = userDocument.filePath;

  if (!filePath) {
    throw new Error('Document missing required filePath field');
  }

  // If filePath is a SharePoint URL but no identifiers, throw error
  // (This shouldn't happen if backend is consistent, but guard against it)
  if (isSharePointUrl(filePath)) {
    console.error('📎 [DocumentPreviewService] filePath is SharePoint URL but missing identifiers');
    const error: ExtendedError = new Error('SharePoint document missing identifiers - please refresh project context');
    error.code = 'MISSING_IDENTIFIERS';
    throw error;
  }

  // If filePath is a direct URL (starts with http:// or https://), return it directly
  if (isDirectUrl(filePath)) {
    return {
      sasUrl: filePath,
      filePath: filePath,
      expiresAt: null,
      permissions: 'r'
    };
  }

  // Otherwise, it's a blob storage path - get SAS token from file-access service
  const baseUrl: string = getFileAccessBaseUrl();

  // Phase 4 contract: single filePath parameter
  // Backend expects snake_case 'file_path' and returns camelCase response
  const response: Response = await authenticatedFetch(`${baseUrl}/api/file-access/sas/download`, {
    method: 'POST',
    body: JSON.stringify({ file_path: filePath })
  });

  if (!response.ok) {
    const errorText: string = await response.text();
    throw new Error(`Failed to get download SAS: ${response.status} ${response.statusText} - ${errorText}`);
  }

  return response.json();
};

/**
 * Download document content using SAS URL
 */
export const downloadDocumentContent = async (sasUrl?: string): Promise<Blob> => {
  const response: Response = await fetch(sasUrl, {
    method: 'GET',
    cache: 'no-store',  // Prevent browser caching (fixes Edge refresh issues)
  });

  if (!response.ok) {
    throw new Error(`Failed to download document: ${response.status} ${response.statusText}`);
  }

  return response.blob();
};

/**
 * Get document preview data (SAS token + content)
 */
export const getDocumentPreview = async (
  userDocument: UserDocument,
  projectId?: string,
  username?: string
): Promise<DocumentPreviewResult> => {
  // First get the SAS token (or SharePoint marker)
  const sasData: SASData = await getDocumentDownloadSAS(userDocument, projectId, username);

  // Check if this is a SharePoint document
  if (sasData.isSharePoint) {
    // Download directly from SharePoint using Graph API
    const contentBlob: Blob = await downloadFromSharePoint(userDocument);

    return {
      sasData: { ...sasData, source: 'sharepoint' },
      contentBlob,
      document: userDocument
    };
  }

  // Otherwise, download using SAS URL
  // Backend returns camelCase 'sasUrl' (Phase 4: no fallbacks)
  const contentBlob: Blob = await downloadDocumentContent(sasData.sasUrl);

  return {
    sasData,
    contentBlob,
    document: userDocument
  };
};

/**
 * Get document preview data with progress tracking (for SharePoint documents)
 */
export const getDocumentPreviewWithProgress = async (
  userDocument: UserDocument,
  projectId?: string,
  username?: string,
  onProgress?: DownloadProgressCallback
): Promise<DocumentPreviewResult> => {
  const sasData: SASData = await getDocumentDownloadSAS(userDocument, projectId, username);

  if (sasData.isSharePoint) {
    // Download from SharePoint with progress tracking
    const contentBlob: Blob = await downloadFromSharePointWithProgress(userDocument, onProgress);

    return {
      sasData: { ...sasData, source: 'sharepoint' },
      contentBlob,
      document: userDocument
    };
  }

  // For non-SharePoint, use regular download (no progress needed - we use URL directly for PDFs)
  const contentBlob: Blob = await downloadDocumentContent(sasData.sasUrl);

  return {
    sasData,
    contentBlob,
    document: userDocument
  };
};

/**
 * Determine if a document can be previewed
 */
export const canPreviewDocument = (userDocument: UserDocument): boolean => {
  if (!userDocument) {
    return false;
  }

  const filename: string | undefined = userDocument.originalFilename;
  if (!filename) {
    return false;
  }

  const filenameLower: string = filename.toLowerCase();
  const supportedExtensions: string[] = ['.pdf', '.md', '.markdown', '.txt', '.yaml', '.yml'];

  if (supportedExtensions.some((ext: string) => filenameLower.endsWith(ext))) {
    return true;
  }

  // Office formats are previewable via Graph API PDF conversion when SharePoint IDs are available
  if (GRAPH_CONVERTIBLE_EXTENSIONS.some((ext: string) => filenameLower.endsWith(ext))) {
    return hasSharePointIdentifiers(userDocument);
  }

  return false;
};

/**
 * Get document type for preview rendering
 */
export const getDocumentPreviewType = (userDocument: UserDocument): string => {
  if (!userDocument) {
    return 'unknown';
  }

  const filename: string | undefined = userDocument.originalFilename;
  if (!filename) {
    return 'unknown';
  }

  const filenameLower: string = filename.toLowerCase();

  if (filenameLower.endsWith('.pdf')) {
    return 'pdf';
  } else if (GRAPH_CONVERTIBLE_EXTENSIONS.some((ext: string) => filenameLower.endsWith(ext))) {
    return 'pdf';
  } else if (filenameLower.endsWith('.md') || filenameLower.endsWith('.markdown')) {
    return 'markdown';
  } else if (filenameLower.endsWith('.txt')) {
    return 'text';
  } else if (filenameLower.endsWith('.yaml') || filenameLower.endsWith('.yml')) {
    return 'yaml';
  }

  return 'unknown';
};
